import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Plus, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { TagDialog } from "../tags/TagDialog";
import {
  addEnchargeTags,
  createEnchargeTag,
  fetchEnchargeTags,
  removeEnchargeTags,
} from "./encharge/api";
import type { EnchargeInstance, EnchargeTag } from "./encharge/types";

const DEFAULT_TAG_COLOR = "#cbd5e1";

function tagColorMap(catalog: EnchargeTag[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const t of catalog) {
    if (t.tag) map.set(t.tag, t.color || DEFAULT_TAG_COLOR);
  }
  return map;
}

export const EnchargeTagsEditor = ({
  instance,
  tags,
  onChange,
  personEmail,
  personId,
  persistImmediately = false,
  disabled = false,
}: {
  instance: EnchargeInstance;
  tags: string[];
  onChange: (tags: string[]) => void;
  personEmail?: string;
  personId?: string;
  /** When true and personEmail/personId set, add/remove calls Encharge immediately. */
  persistImmediately?: boolean;
  disabled?: boolean;
}) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingTag, setPendingTag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: catalog = [], isPending: catalogPending } = useQuery({
    queryKey: ["email-lists", "tags", instance],
    queryFn: () => fetchEnchargeTags(instance),
    staleTime: 60 * 1000,
  });

  const colors = useMemo(() => tagColorMap(catalog), [catalog]);

  const selected = tags ?? [];
  const unselected = catalog.filter(
    (t) => t.tag && !selected.includes(t.tag),
  );

  const canPersist =
    persistImmediately && !!(personEmail?.trim() || personId?.trim());

  const invalidateTags = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["email-lists", "tags", instance],
    });
  }, [queryClient, instance]);

  const addMutation = useMutation({
    mutationFn: async (tagName: string) => {
      if (canPersist) {
        await addEnchargeTags(instance, {
          tags: [tagName],
          email: personEmail,
          personId,
        });
      }
      return tagName;
    },
    onMutate: (tagName) => {
      setPendingTag(tagName);
      setError(null);
    },
    onSuccess: (tagName) => {
      if (!selected.includes(tagName)) {
        onChange([...selected, tagName]);
      }
    },
    onError: (err: Error) => setError(err.message),
    onSettled: () => setPendingTag(null),
  });

  const removeMutation = useMutation({
    mutationFn: async (tagName: string) => {
      if (canPersist) {
        await removeEnchargeTags(instance, {
          tags: [tagName],
          email: personEmail,
          personId,
        });
      }
      return tagName;
    },
    onMutate: (tagName) => {
      setPendingTag(tagName);
      setError(null);
    },
    onSuccess: (tagName) => {
      onChange(selected.filter((t) => t !== tagName));
    },
    onError: (err: Error) => setError(err.message),
    onSettled: () => setPendingTag(null),
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; color: string }) => {
      const created = await createEnchargeTag(instance, {
        tag: data.name,
        color: data.color,
      });
      const tagName = created[0]?.tag ?? data.name;
      if (canPersist) {
        await addEnchargeTags(instance, {
          tags: [tagName],
          email: personEmail,
          personId,
        });
      }
      return tagName;
    },
    onSuccess: (tagName) => {
      invalidateTags();
      if (!selected.includes(tagName)) {
        onChange([...selected, tagName]);
      }
      setCreateOpen(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  const busy = addMutation.isPending || removeMutation.isPending || createMutation.isPending;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1.5 items-center">
        {selected.map((tagName) => (
          <div
            key={tagName}
            className="text-black inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md"
            style={{
              backgroundColor: colors.get(tagName) || DEFAULT_TAG_COLOR,
            }}
          >
            {tagName}
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => removeMutation.mutate(tagName)}
              className="transition-colors p-0 ml-0.5 cursor-pointer disabled:opacity-50"
              aria-label={translate("crm.email_lists.tags.remove", {
                _: "Remove tag",
              })}
            >
              {pendingTag === tagName && removeMutation.isPending ? (
                <Spinner className="w-3 h-3" />
              ) : (
                <X className="w-3 h-3" />
              )}
            </button>
          </div>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs cursor-pointer"
              disabled={disabled || busy || catalogPending}
            >
              <Plus className="w-3 h-3 mr-1" />
              {translate("crm.email_lists.tags.add", { _: "Add tag" })}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
            {unselected.map((tag) => (
              <DropdownMenuItem
                key={tag.tag}
                onClick={() => addMutation.mutate(tag.tag)}
                disabled={busy}
              >
                <Badge
                  variant="secondary"
                  className="text-xs font-normal text-black"
                  style={{
                    backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                  }}
                >
                  {tag.tag}
                </Badge>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              onClick={() => {
                setError(null);
                setCreateOpen(true);
              }}
            >
              <span className="flex items-center gap-2 text-sm">
                <Edit className="w-3 h-3" />
                {translate("crm.email_lists.tags.create", {
                  _: "Create new tag",
                })}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {error ? <p className="text-destructive text-xs">{error}</p> : null}

      <TagDialog
        open={createOpen}
        title={translate("crm.email_lists.tags.create_title", {
          _: "Create tag",
        })}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data);
        }}
      />
    </div>
  );
};
